import { runInAction } from "mobx";
import type {
  IWarehouseApi,
  RegisterPendingResponse,
  TelegramWidgetAuthInput,
} from "../../../api/WarehouseApi";
import { type INavigationStore, Screens } from "../../../stores/navigation";
import type { IRealtimeStore } from "../../../stores/RealtimeStore";
import { hasStoredAccessToken } from "../../../utils/authStorage";
import {
  getTelegramWebAppInitData,
  type TelegramWidgetAuthUser,
} from "../../../utils/telegramWebApp";

export interface LoginWorkflowState {
  username: string;
  password: string;
  isSubmitting: boolean;
  isTelegramSubmitting: boolean;
  isGoogleSubmitting: boolean;
  isAppleSubmitting: boolean;
  isRegisterMode: boolean;
  error: string | null;
  notice: string | null;
  telegramInitData: string;
}

function clearAuthMessages(state: LoginWorkflowState): void {
  state.error = null;
  state.notice = null;
}

function isPendingResponse(
  response: unknown,
): response is RegisterPendingResponse {
  if (!response || typeof response !== "object") {
    return false;
  }

  const data = response as Record<string, unknown>;
  return data.pending === true && typeof data.message === "string";
}

function resolveAuthError(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function onAuthSuccess(input: {
  realtimeStore: Pick<IRealtimeStore, "reconnect">;
  navigationStore: Pick<INavigationStore, "setScreen">;
}): void {
  input.realtimeStore.reconnect();
  input.navigationStore.setScreen(Screens.Main);
}

async function executeAuthOperation(input: {
  state: LoginWorkflowState;
  setSubmitting: (value: boolean) => void;
  request: () => Promise<unknown>;
  fallbackError: string;
  realtimeStore: Pick<IRealtimeStore, "reconnect">;
  navigationStore: Pick<INavigationStore, "setScreen">;
  onPending?: (response: RegisterPendingResponse) => void;
}): Promise<void> {
  runInAction(() => {
    input.setSubmitting(true);
    clearAuthMessages(input.state);
  });

  try {
    const response = await input.request();
    if (isPendingResponse(response)) {
      runInAction(() => {
        if (input.onPending) {
          input.onPending(response);
        } else {
          input.state.notice = response.message;
        }
      });
      return;
    }

    onAuthSuccess({
      realtimeStore: input.realtimeStore,
      navigationStore: input.navigationStore,
    });
  } catch (error) {
    runInAction(() => {
      input.state.error = resolveAuthError(error, input.fallbackError);
    });
  } finally {
    runInAction(() => {
      input.setSubmitting(false);
    });
  }
}

function toTelegramWidgetAuthInput(
  payload: TelegramWidgetAuthUser,
): TelegramWidgetAuthInput {
  return {
    id: payload.id,
    first_name: payload.first_name,
    last_name: payload.last_name,
    username: payload.username,
    photo_url: payload.photo_url,
    auth_date: payload.auth_date,
    hash: payload.hash,
  };
}

export function initializeLoginOperation(input: {
  state: LoginWorkflowState;
  navigationStore: Pick<INavigationStore, "setScreen">;
  realtimeStore: Pick<IRealtimeStore, "reconnect">;
}): void {
  if (hasStoredAccessToken()) {
    input.navigationStore.setScreen(Screens.Main);
    input.realtimeStore.reconnect();
    return;
  }

  runInAction(() => {
    input.state.telegramInitData = getTelegramWebAppInitData();
  });
}

export function toggleLoginModeOperation(input: {
  state: LoginWorkflowState;
}): void {
  input.state.isRegisterMode = !input.state.isRegisterMode;
  clearAuthMessages(input.state);
  input.state.telegramInitData = getTelegramWebAppInitData();
}

export function validateLoginCredentials(
  state: LoginWorkflowState,
): string | null {
  if (state.username.trim().length < 2) {
    return "Логин или email должны содержать минимум 2 символа";
  }

  if (state.password.length < 5) {
    return "Пароль должен содержать минимум 5 символов";
  }

  return null;
}

export async function submitCredentialsOperation(input: {
  state: LoginWorkflowState;
  api: Pick<IWarehouseApi, "register" | "login">;
  navigationStore: Pick<INavigationStore, "setScreen">;
  realtimeStore: Pick<IRealtimeStore, "reconnect">;
}): Promise<void> {
  const validationError = validateLoginCredentials(input.state);
  if (validationError) {
    input.state.error = validationError;
    return;
  }

  await executeAuthOperation({
    state: input.state,
    setSubmitting: (value) => {
      input.state.isSubmitting = value;
    },
    request: async () => {
      const username = input.state.username.trim();
      const password = input.state.password;
      if (input.state.isRegisterMode) {
        return input.api.register({ username, password });
      }

      return input.api.login({ username, password });
    },
    fallbackError: input.state.isRegisterMode
      ? "Не удалось зарегистрироваться"
      : "Не удалось выполнить вход",
    navigationStore: input.navigationStore,
    realtimeStore: input.realtimeStore,
    onPending: (response) => {
      input.state.password = "";
      input.state.isRegisterMode = false;
      input.state.notice = response.message;
    },
  });
}

export async function submitTelegramMiniAppOperation(input: {
  state: LoginWorkflowState;
  api: Pick<IWarehouseApi, "loginWithTelegram">;
  navigationStore: Pick<INavigationStore, "setScreen">;
  realtimeStore: Pick<IRealtimeStore, "reconnect">;
  canSubmit: boolean;
}): Promise<void> {
  if (!input.canSubmit) {
    input.state.error =
      "Telegram-вход недоступен: откройте форму внутри Telegram Mini App.";
    return;
  }

  await executeAuthOperation({
    state: input.state,
    setSubmitting: (value) => {
      input.state.isTelegramSubmitting = value;
    },
    request: async () =>
      input.api.loginWithTelegram(input.state.telegramInitData),
    fallbackError: "Не удалось выполнить вход через Telegram",
    navigationStore: input.navigationStore,
    realtimeStore: input.realtimeStore,
  });
}

export async function submitTelegramWidgetOperation(input: {
  state: LoginWorkflowState;
  api: Pick<IWarehouseApi, "loginWithTelegramWidget">;
  navigationStore: Pick<INavigationStore, "setScreen">;
  realtimeStore: Pick<IRealtimeStore, "reconnect">;
  payload: TelegramWidgetAuthUser;
  isAuthLocked: boolean;
}): Promise<void> {
  if (input.isAuthLocked) {
    return;
  }

  const widgetPayload = toTelegramWidgetAuthInput(input.payload);

  await executeAuthOperation({
    state: input.state,
    setSubmitting: (value) => {
      input.state.isTelegramSubmitting = value;
    },
    request: async () => input.api.loginWithTelegramWidget(widgetPayload),
    fallbackError: "Не удалось выполнить вход через Telegram",
    navigationStore: input.navigationStore,
    realtimeStore: input.realtimeStore,
  });
}

export async function submitGoogleIdTokenOperation(input: {
  state: LoginWorkflowState;
  api: Pick<IWarehouseApi, "loginWithGoogleIdToken">;
  navigationStore: Pick<INavigationStore, "setScreen">;
  realtimeStore: Pick<IRealtimeStore, "reconnect">;
  idToken: string;
  canSubmit: boolean;
}): Promise<void> {
  if (!input.canSubmit) {
    return;
  }

  await executeAuthOperation({
    state: input.state,
    setSubmitting: (value) => {
      input.state.isGoogleSubmitting = value;
    },
    request: async () => input.api.loginWithGoogleIdToken(input.idToken),
    fallbackError: "Не удалось выполнить вход через Google",
    navigationStore: input.navigationStore,
    realtimeStore: input.realtimeStore,
  });
}

export async function submitAppleIdTokenOperation(input: {
  state: LoginWorkflowState;
  api: Pick<IWarehouseApi, "loginWithAppleIdToken">;
  navigationStore: Pick<INavigationStore, "setScreen">;
  realtimeStore: Pick<IRealtimeStore, "reconnect">;
  idToken: string;
  canSubmit: boolean;
}): Promise<void> {
  if (!input.canSubmit) {
    return;
  }

  await executeAuthOperation({
    state: input.state,
    setSubmitting: (value) => {
      input.state.isAppleSubmitting = value;
    },
    request: async () => input.api.loginWithAppleIdToken(input.idToken),
    fallbackError: "Не удалось выполнить вход через Apple",
    navigationStore: input.navigationStore,
    realtimeStore: input.realtimeStore,
  });
}
